import React, { useState } from "react";
import {
  PathogenDetectionAdvisoryResult,
  TimeSeries,
} from "../models/advistory";
import { Legend, RangeTabs, RiskTrendChart } from "./RiskTrendChart";
import { Image, Text, View } from "react-native";
import {
  BG_STYLES,
  BORDER_STYLES,
  COMMON_STYLES,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import images from "../assets/image";
import { formatDateTime, normalizePercent } from "../utils/chart";

interface Props {
  pathogenData: PathogenDetectionAdvisoryResult;
}

const PathogenDetectionCard = ({ pathogenData }: Props) => {
  const [range, setRange] = useState<keyof TimeSeries>("24h");
  return (
    <>
      <View
        style={[
          COMMON_STYLES["ph3/2"],
          COMMON_STYLES.pv1,
          BG_STYLES.bgWhite,
          {
            gap: 8,
            justifyContent: "space-between",
          },
          COMMON_STYLES.row,
          COMMON_STYLES.alignItemsCenter,
        ]}
      >
        <Image source={images.PathogenRisk} style={{ width: 40, height: 40 }} />
        <Text
          style={[
            TYPOGRAPHY.TEXT1,
            VARIANTS_STYLES.SEMI_EMPHASIZED,
            TEXT_STYLES.textNeutral100,
          ]}
        >
          Real-time Pathogen Risk
        </Text>
        <View
          style={[
            COMMON_STYLES["pv3/8"],
            COMMON_STYLES["ph3/4"],
            COMMON_STYLES.br1,
            BG_STYLES.bgSuccess120,
            COMMON_STYLES.justifyCenter,
            COMMON_STYLES.row,
            COMMON_STYLES.alignItemsCenter,
            { gap: 4 },
          ]}
        >
          <View
            style={[
              COMMON_STYLES.br100,
              { width: 8, height: 8 },
              BG_STYLES.bgSuccess100,
            ]}
          ></View>
          <Text
            style={[
              TYPOGRAPHY.CAPTION1,
              VARIANTS_STYLES.SEMI_EMPHASIZED,
              TEXT_STYLES.textSuccess100,
            ]}
          >
            Live
          </Text>
        </View>
      </View>
      <View
        style={[
          COMMON_STYLES["pv1/2"],
          COMMON_STYLES["ph3/2"],
          COMMON_STYLES.row,
          { gap: 8, justifyContent: "space-between" },
          COMMON_STYLES.alignItemsCenter,
        ]}
      >
        <Image
          source={images.PathogenClock}
          style={{ width: 16, height: 16 }}
        />

        <Text
          style={[
            TYPOGRAPHY.CAPTION1,
            VARIANTS_STYLES.BASE,
            { color: "#6B7280" },
          ]}
        >{`Last updated : ${formatDateTime(pathogenData.last_updated)}`}</Text>
        {pathogenData.pm.risk === "HIGH" || pathogenData.dm.risk === "HIGH" ? (
          <View
            style={[COMMON_STYLES.row, { gap: 4, justifyContent: "flex-end" }]}
          >
            <Image
              source={images.PathogenElevated}
              style={{ width: 18, height: 16 }}
            />
            <Text
              style={[
                TYPOGRAPHY.CAPTION1,
                VARIANTS_STYLES.SEMI_EMPHASIZED,
                { color: "#F0A72B" },
              ]}
            >
              Elevated Risk
            </Text>
          </View>
        ) : null}
      </View>
      <View
        style={[COMMON_STYLES.ph1, COMMON_STYLES.pb1, BG_STYLES.bgWhite, {}]}
      >
        <View
          style={[
            { justifyContent: "space-between", borderBottomWidth: 1 },
            COMMON_STYLES.alignItemsCenter,
            BORDER_STYLES.borderNeutral20,
            COMMON_STYLES["pv3/4"],
            COMMON_STYLES.row,
          ]}
        >
          <View
            style={[
              COMMON_STYLES.row,
              COMMON_STYLES.alignItemsCenter,
              { gap: 8 },
            ]}
          >
            <Image
              source={images.PowderyMildew}
              style={{ width: 40, height: 40 }}
            />
            <Text
              style={[
                TYPOGRAPHY.TEXT1,
                VARIANTS_STYLES.SEMI_EMPHASIZED,
                TEXT_STYLES.textNeutral100,
              ]}
            >
              Powdery Mildew
            </Text>
          </View>
          <View
            style={[
              COMMON_STYLES.row,
              COMMON_STYLES.alignItemsCenter,
              { gap: 8 },
            ]}
          >
            <View
              style={[
                COMMON_STYLES.br1,
                BG_STYLES.bgYellow20,
                COMMON_STYLES["pv3/8"],
                COMMON_STYLES["ph3/4"],
                COMMON_STYLES.justifyCenter,
                COMMON_STYLES.alignItemsCenter,
              ]}
            >
              <Text
                style={[
                  TYPOGRAPHY.TEXT1,
                  VARIANTS_STYLES.SEMI_EMPHASIZED,
                  TEXT_STYLES.textYellow120,
                ]}
              >
                {pathogenData.pm.risk}
              </Text>
            </View>
            <Text
              style={[
                TYPOGRAPHY.TEXT1,
                VARIANTS_STYLES.SEMI_EMPHASIZED,
                TEXT_STYLES.textNeutral100,
              ]}
            >
              {`${normalizePercent(pathogenData.pm.percent)}%`}
            </Text>
          </View>
        </View>
        <View
          style={[
            { justifyContent: "space-between", borderBottomWidth: 1 },
            COMMON_STYLES.alignItemsCenter,
            BORDER_STYLES.borderNeutral20,
            COMMON_STYLES["pv3/4"],
            COMMON_STYLES.row,
          ]}
        >
          <View
            style={[
              COMMON_STYLES.row,
              COMMON_STYLES.alignItemsCenter,
              { gap: 8 },
            ]}
          >
            <Image
              source={images.DownyMildew}
              style={{ width: 40, height: 40 }}
            />
            <Text
              style={[
                TYPOGRAPHY.TEXT1,
                VARIANTS_STYLES.SEMI_EMPHASIZED,
                TEXT_STYLES.textNeutral100,
              ]}
            >
              Downy Mildew
            </Text>
          </View>
          <View
            style={[
              COMMON_STYLES.row,
              COMMON_STYLES.alignItemsCenter,
              { gap: 8 },
            ]}
          >
            <View
              style={[
                COMMON_STYLES.br1,
                BG_STYLES.bgYellow20,
                COMMON_STYLES["pv3/8"],
                COMMON_STYLES["ph3/4"],
                COMMON_STYLES.justifyCenter,
                COMMON_STYLES.alignItemsCenter,
              ]}
            >
              <Text
                style={[
                  TYPOGRAPHY.TEXT1,
                  VARIANTS_STYLES.SEMI_EMPHASIZED,
                  TEXT_STYLES.textYellow120,
                ]}
              >
                {pathogenData.dm.risk}
              </Text>
            </View>
            <Text
              style={[
                TYPOGRAPHY.TEXT1,
                VARIANTS_STYLES.SEMI_EMPHASIZED,
                TEXT_STYLES.textNeutral100,
              ]}
            >
              {`${normalizePercent(pathogenData.dm.percent)}%`}
            </Text>
          </View>
        </View>
        <RangeTabs value={range} onChange={setRange} />
        <Legend />

        <RiskTrendChart data={pathogenData.timeseries[range]} />
      </View>
    </>
  );
};

export default PathogenDetectionCard;
